import * as PIXI from 'pixi.js';
import * as TweenJS from "slotworks/utils/tween";
import { Signal } from "signals";
import Wheel from './wheel-animated';
import Layers from 'slotworks/graphics/layers/layers';
import settingsModel from 'slotworks/model/settings/settings-model';
import soundManager from 'slotworks/sound/sound-manager';
import { ButtonEvent } from 'slotworks/graphics/elements/button-element';
import Contract from 'slotworks/utils/contract';
import { parallel, sequence } from 'slotworks/utils/race-utils';
import deviceInfo from 'slotworks/utils/device-info';
import Components from 'slotworks/components/components';
import gameState from 'slotworks/model/game-state';
import WheelBonusRecord from 'integration/records/wheel-bonus-record';
import commsManager from 'slotworks/server/comms-manager';
import FreespinRequestPayload from 'slotworks/model/gameplay/requests/freespin-request-payload';
import { HideBaseLayers, ShowBaseLayers, ShowBaseLayersFull, ShowFSLayersFull } from 'commands/base-layers-commands';
import HarleyComponents from "components/components";
import currency from 'slotworks/i18n/currency';
import WheelBonusResult from 'integration/results/wheel-bonus-result';
import { CenterPivot, PIXIElement } from 'slotworks/graphics/pixi/group';
import AnimatedSprite from 'slotworks/graphics/animation/animated-sprite';
import regulation from 'slotworks/integration/revolver/regulations/regulation';
import { MoveLayerInFront } from 'model/move-layer-in-front';
import { MoveLayerBehind } from 'model/move-layer-behind';

export default class BonusBuyWheelComponent {
    // public onTriggered: Signal = new Signal();
    public onComplete: Signal = new Signal();

    private wheel: Wheel;
    private hand: PIXI.Sprite;
    private handTween: TweenJS.Tween<PIXI.Sprite> = null;

    private layer: Layers;

    private fullResolve: () => void = null;
    private value: PIXI.Text;

    private two: PIXI.Sprite;
    private three: PIXI.Sprite;
    private turnsVisible: PIXI.Sprite;
    private total: PIXI.Text;
    private totalValue: number = 0;
    private multiwheel: boolean = false;

    private tallyActiveAnimation: AnimatedSprite; // anim-TallyMeter_BG_Active-false
    private tallyWinAnimation: AnimatedSprite; // anim-TallyMeter_BG_Win-false

    private glowStack: PIXIElement;
    private glowTweens: TweenJS.Tween<any>[] = [];
    private glows: PIXI.Sprite[] = [];

    private initialSpin: boolean = true;

    public start(recovery: boolean = false) {
        return new Contract(resolve => {
            this.initialSpin = true;
            this.layer = Layers.Bonus;
            HarleyComponents.quickHit.stop();
            // Layers.Background.setScene("bonus").execute();
            parallel([
                () => this.displayPrompt(recovery),
                () => this.runBonus(recovery),
            ]).then(() => {
                const record = gameState.getCurrentGame().latestRecord() as WheelBonusRecord;
                record.beenPaid = true;                
                Layers.Custom.get("Harley").moveBehind(Layers.Custom.get("Transition"));
                resolve(null);
            });
        })
    }

    private runBonus(recovery: boolean = false) {
        return new Contract<void>(resolve => {
            this.fullResolve = resolve;
            HarleyComponents.background.startBonus();
            setTimeout(() => {
                this.enterBonus();
            }, recovery ? 0 : 1500);
        });
    }

    private celebrateWin() {
        return new Contract<void>(resolve => {      
            HarleyComponents.background.startBonusSelection();      
            const record = gameState.getCurrentGame().getCurrentRecord() as WheelBonusRecord;         

            this.value.visible = true;
            this.value.scale.set(0, 0);

            const valuePosition = Layers.BonusForeground.getPosition("value");
            const valueEndPosition = Layers.BonusForeground.getPosition("valueEnd");

            const tweenObj = {
                scale: 0,
                // x: this.value.x,
                y: valuePosition.y,
                value: 0
            };
            const update = () => {
                this.value.scale.set(tweenObj.scale, tweenObj.scale);
                this.value.y = tweenObj.y;
                this.value.text = currency.format(tweenObj.value);
            }
            update();
            this.startGlowTweens();
            new TweenJS.Tween(tweenObj)
                .to({ scale: 1.3, y: valueEndPosition.y + valueEndPosition.height, value: record.creditsWon }, 500)
                .easing(TweenJS.Easing.Back.Out)
                .onUpdate(update)
                .onComplete(() => {
                    this.value.text = currency.format(record.creditsWon);                    
                    if (record.total == 1) {                        
                        setTimeout(() => {
                            this.value.visible = false;
                            this.endGlowTweens(true);
                            resolve(null)
                        }, 3300);
                    } else {                        
                        sequence([
                            () => Contract.getTimeoutContract(600),
                            () => Contract.wrap(() => {
                                HarleyComponents.harley.clap();
                                this.endGlowTweens();
                            }),
                            () => this.swipeWin(),
                            () => Contract.wrap(() => {
                                this.tallyActiveAnimation.stop();
                                this.tallyActiveAnimation.visible = false;
                                this.tallyWinAnimation.gotoAndPlay(0);
                                this.tallyWinAnimation.loop = true;
                                this.tallyWinAnimation.visible = true;
                            }),
                            () => this.countUpTotal(record.totalCreditsWon),
                        ]).then(resolve);
                    }
                }).start();
        });
    }

    private swipeWin() {
        return new Contract<void>(resolve => {
            // position total
            const endPosition = Layers.BonusForeground.getPosition("total");

            const tweenObj = {
                scale: 1.1,
                x: this.value.x,
                y: this.value.y
            };
            const update = () => {
                this.value.scale.set(tweenObj.scale, tweenObj.scale);
                this.value.y = tweenObj.y;
            }
            new TweenJS.Tween(tweenObj)
                .to({ scale: 0.1, x: endPosition.x, y: endPosition.y })
                .delay(100)
                .easing(TweenJS.Easing.Back.In)
                .onUpdate(update)
                .onComplete(() => {
                    this.value.visible = false;
                    resolve(null);
                }).start();
        });
    }

    private countUpTotal(value: number) {
        return new Contract<void>(resolve => {
            const tweenObj = { total: this.totalValue };
            const update = () => {
                this.total.text = currency.format(tweenObj.total);
            }
            new TweenJS.Tween(tweenObj)
                .to({ total: value }, 800)
                .onUpdate(update)
                .onComplete(() => {
                    this.tallyWinAnimation.stop();
                    this.tallyWinAnimation.visible = false;
                    new TweenJS.Tween(this.total.scale)
                    .to({x: 1.1, y: 1.1}, 200)
                    .onComplete(() => {
                        this.total.scale.set(1, 1);
                        resolve(null);
                    })
                    .repeat(1)
                    .yoyo(true)
                    .start();                    
                })
                .start()
        });
    }

    private displayPrompt(recovery: boolean = false) {
        HideBaseLayers();

        if(recovery) {
            soundManager.customEvent("bonus_music");
            return Layers.Background.setScene("bonus");
        }

        let count = 0;
        const record = gameState.getCurrentGame().getCurrentRecord();
        if (record instanceof WheelBonusRecord) {
            count = record.total;
        } else {
            const results = record.getResultsOfType(WheelBonusResult);
            if (results.length) {
                const result = results[0] as WheelBonusResult;
                count = result.total;
            }
        }

        return sequence([
            () =>  Layers.Background.setScene("bonus"),
            () => Layers.Custom.get("Transition").setScene("turns"),
            () => Contract.wrap(() => soundManager.customEvent("bonus_music")),
            () => Layers.Custom.get("Transition").defaultScene(),
        ]);
    }

    private enterBonus() {
        const ready = () => this.waitForInput();

        const finish = () => {            
            this.celebrateWin().then(() => {
                const record = gameState.getCurrentGame().getCurrentRecord() as WheelBonusRecord;
                
                if (record.is_complete) {
                    deviceInfo.onLayoutChange.remove(this.onLayoutChange);
                    if (this.fullResolve) {
                        const resolve = this.fullResolve;
                        this.fullResolve = null;
                        resolve();
                    }
                } else {
                    this.setTurns();
                    this.setTotal();
                    this.wheel.onComplete.addOnce(finish);
                    this.wheel.onReady.addOnce(ready);
                    this.wheel.restart();
                }
            });
        };



        this.setup().then(() => {
            this.tallyActiveAnimation = Layers.BonusForeground.getAnimatedSprite("TallyMeter_BG_Active");
            this.tallyWinAnimation = Layers.BonusForeground.getAnimatedSprite("TallyMeter_BG_Win");
            this.hideGlows();
            if (this.getTotalTurns() == 1) {
                this.multiwheel = false;
                this.hideTallyMeterOnSingle();
            } else {
                // active
                this.multiwheel = true;
            }
            this.setTurns(true);
            this.setTotal();
            this.value = Layers.BonusForeground.getText("value");
            this.value.visible = false;
            this.hand = Layers.BonusForeground.getSprite("hand_icon");
            this.hand.visible = false;
            this.wheel = new Wheel();
            this.wheel.onComplete.addOnce(finish);
            this.wheel.onReady.addOnce(ready);
            if (!deviceInfo.isDesktop) {
                deviceInfo.onLayoutChange.add(this.onLayoutChange);
            }
        });
    }

    private onLayoutChange = () => {
        setTimeout(() => {
            if (this.handTween) {
                this.startHand();
            }
        }, 10);
    }

    private getTotalTurns() {
        const record = gameState.getCurrentGame().getCurrentRecord();
        let turns = 0;
        if (record instanceof WheelBonusRecord) {
            turns = record.total;
        } else {
            const results = record.getResultsOfType(WheelBonusResult) as WheelBonusResult[];
            if (results.length > 0) {
                turns = results[0].total;
            }
        }
        return turns;
    }

    private setup() {        
        // Layers.CelebrationBackground.moveBehind(this.layer);
        MoveLayerBehind(Layers.Custom.get("Harley"), Layers.BonusForeground);
        HarleyComponents.harley.showIdleFreespins();
        HarleyComponents.harley.show(undefined, undefined, true);

        return parallel([
            // () => Layers.CelebrationBackground.setScene("bonuswheel"),
            () => Layers.BonusForeground.setScene("bonuswheel"),
            // () => this.layer.setScene("bonuswheel"),
            () => this.layer.setScene("bonuswheelnew")
        ]);
    }

    private hideTallyMeterOnSingle() {
        // tally_plaque_shadow
        // TallyMeter_BG_Static
        // anim-TallyMeter_BG_Win-false, anim-TallyMeter_BG_Active-false
        // Frame
        // total
        const TALLY_METER_SPRITES = [
            "tally_plaque_shadow",
            "TallyMeter_BG_Static",
            "TallyMeter_BG_Win",
            "TallyMeter_BG_Active",
            "Frame",
            "total",
            "totalText"
        ];
        for (let s of TALLY_METER_SPRITES) {
            Layers.BonusForeground.getSprite(s).visible = false;
        }
    }

    private waitForInput() {
        soundManager.customEvent("bonus_music");
        const onKeyDown = (event: any) => {
            if (settingsModel.getSpaceToSpin()) {
                if (event.keyCode === 32) {
                    onClick();
                }
            }
        };

        const onClick = () => {
            this.initialSpin = false;
            document.removeEventListener("keyup", onKeyDown);
            document.body.removeEventListener(ButtonEvent.POINTER_DOWN.getEventString(), onClick, true);
            this.layer.container.buttonMode = false;
            this.layer.container.interactive = false;
            if(this.handTween) {
                TweenJS.remove(this.handTween);
            }

            this.handTween = null;
            this.hand.visible = false;
            HarleyComponents.background.startBonusSpin();
            soundManager.customEvent("bonus_music_soft");
            this.wheel.start();
            this.requestData();            
            if(this.multiwheel) {
                this.tallyActiveAnimation.visible = true;
                this.tallyActiveAnimation.gotoAndPlay(0); 
                this.tallyActiveAnimation.loop = true;           
            }
        };

        if(this.initialSpin) {
            this.callToAction().then(() => {
                setTimeout(() => {
                    document.addEventListener("keyup", onKeyDown);
                    document.body.addEventListener(ButtonEvent.POINTER_DOWN.getEventString(), onClick, true);
                    this.layer.container.on(ButtonEvent.POINTER_UP.getEventString(), onClick);
                    this.layer.container.interactive = true;
                    this.layer.container.buttonMode = true;
                }, 100);
            });
        } else {
            onClick();
        }
    }

    private requestData() {
        commsManager.request(new FreespinRequestPayload()).then(() => {
            HarleyComponents.harley.spin1();
            const record = gameState.getCurrentGame().latestRecord() as WheelBonusRecord;
            this.feedResult(record.giftedAdditional);
        });
    }

    public zoomOut() {
        return this.wheel.zoomOut();
    }

    private callToAction(): Contract<void> {
        return new Contract<void>(resolve => {
            this.startHand();
            resolve(null);
        });
    }

    public feedResult(amount: number) {
        const result = `${amount}`;
        this.wheel.setWinner(result);
    }

    public exitBonus() {
        if (!gameState.getCurrentGame().inFreespinsGame) {
            soundManager.customEvent("bonus_music_stop");
        }
        this.wheel.clean();
        Layers.BonusForeground.defaultScene().execute();
        this.layer.defaultScene().execute();        
        HarleyComponents.harley.hide(true);
        if (!gameState.getCurrentGame().inFreespinsGame) {
            regulation.updateNetPosition(gameState.getNetPosition());
            ShowBaseLayers();
            Layers.Background.defaultScene().execute();
        } else {
            Layers.Background.setScene("freespins").execute();
            ShowFSLayersFull();
        }
        // HarleyComponents.background.start();
    }

    private setTurns(setup: boolean = false) {
        const record = gameState.getCurrentGame().latestRecord();
        let remain = 0;
        let total = 0;
        if (record instanceof WheelBonusRecord) {
            remain = record.remain;
            total = record.total;
        } else {
            const results = record.getResultsOfType(WheelBonusResult);
            if (results.length) {
                const result = results[0] as WheelBonusResult;
                remain = result.remain;
                total = remain;
            }
        }
        this.two = Layers.BonusForeground.getSprite("2");
        this.three = Layers.BonusForeground.getSprite("3");
        if (remain > 2) {
            this.two.visible = false;
            this.turnsVisible = this.three;
        } else if (remain == 2) {
            if(total > remain && !setup) {
                soundManager.customEvent("wheel_indicator");
                new TweenJS.Tween(this.three.scale)
                .to({x: 1.2, y: 1.2}, 230)
                .onComplete(() => {
                    this.three.visible = false;
                    this.two.visible = true;
                    this.turnsVisible = this.two;
                    this.two.scale.set(1.2, 1.2);
                    new TweenJS.Tween(this.two.scale)
                    .delay(30)
                    .to({x: 1, y: 1}, 200)
                    .start();
                })
                .start();
            } else {
                this.three.visible = false;
                this.two.visible = true;
                this.turnsVisible = this.two;
            }
        } else {
            this.two.visible = false;
            this.three.visible = false;
            Layers.BonusForeground.getSprite("turns_text2").visible = false;
            Layers.BonusForeground.getSprite("turns_text1").visible = false;
        }
    }

    private updateTurns() {
        if (this.turnsVisible && this.turnsVisible.visible) {
            this.setTurns();
        }
    }

    private setTotal(instant: boolean = false) {
        instant = true;
        const record = gameState.getCurrentGame().getCurrentRecord();
        this.total = Layers.BonusForeground.getText("total");
        if (record instanceof WheelBonusRecord) {
            if (instant) {
                this.totalValue = record.totalCreditsWon;
                this.total.text = currency.format(this.totalValue);
            } else {

            }
        } else {
            this.total.text = currency.format(0);
        }
    }

    private hideGlows() {
        // glow_1, glow_2,
        for (let i = 1; i <= 4; i++) {
            Layers.BonusForeground.getSprite("glow_" + i).visible = false;
        }
    }

    // scale 1.87
    private startGlowTweens() {
        if(this.glowStack) {
            for (let i = 0; i < this.glows.length; i++) {
                this.glows[i].visible = true;
            }
        } else {
            this.glows = [];
            for (let i = 1; i <= 4; i++) {
                const sprite = Layers.BonusForeground.getSprite("glow_" + i);
                sprite.visible = true;
                sprite.anchor.set(0.5, 0.5);
                sprite.x += sprite.width / 2;
                sprite.y += sprite.height / 2;
                this.glows.push(sprite);
            }
            this.glows = this.glows.reverse();
            this.glowStack = CenterPivot(this.glows);
            for (let i = 0; i < 4; i++) {
                const glow = this.glows[i];
                this.glowTweens.push(new TweenJS.Tween(glow)
                    .to({ rotation: Math.PI * 2 }, 20000 + i * 10000)
                    .repeat(Infinity));
            }
        }        
        new TweenJS.Tween(this.glowStack.scale)
            .to({ x: 1.87, y: 1.87 }, 200)
            .onComplete(() => this.glowTweens.forEach(tween => tween.start()))
            .start();
        this.glowStack.visible = true;
    }

    private endGlowTweens(instant: boolean = false) {
        this.glowTweens.forEach(tween => tween.stop());
        if (instant) {

        } else {
            new TweenJS.Tween(this.glowStack.scale)
                .to({ x: 0, y: 0 }, 200)
                .onComplete(() => { 
                    this.glowStack.visible = false; 
                })
                .start();
        }
    }

    private startHand = () => {
        if (this.handTween) {
            this.handTween.stop();
            TweenJS.remove(this.handTween);
        }
        const start = Layers.BonusForeground.getPosition("start");
        const end = Layers.BonusForeground.getPosition("end");
        this.hand = Layers.BonusForeground.getSprite("hand_icon");
        this.hand.visible = true;
        this.hand.position.set(start.x, start.y);
        this.handTween = new TweenJS.Tween(this.hand)
            .to({ y: end.y }, 800)
            .yoyo(true)
            .repeat(Infinity)
            .start();
    }
}